import React, { useMemo } from "react";
import { useTable, usePagination } from "react-table";
import { GeopolDocument } from "../types";
import { safe_get } from "../../../report-visuals/report-utils";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

interface DocumentListProps {
  documents: GeopolDocument[];
  onRowClick?: (document: GeopolDocument) => void;
  rowTransformers?: Record<string, any>;
}

export const DocumentList: React.FC<DocumentListProps> = ({ documents, onRowClick, rowTransformers = {} }) => {
  const isMapped = (documentId: string) => {
    return documentId in rowTransformers;
  };

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }: any) => {
          const mapped = isMapped(row.original.id);
          return (
            <div className="flex items-center justify-center w-full">
              {mapped && (
                <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" title="This record is mapped" />
              )}
            </div>
          );
        },
      },
      // {
      //   Header: "ID",
      //   accessor: "id",
      //   Cell: ({ value }: any) => {
      //     return (
      //       <span className="text-sm font-mono">{value}</span>
      //     );
      //   },
      // },
      {
        Header: "Name",
        accessor: "demographic_profile_of_respondents.contact_info_name",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.contact_info_name",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return (
            <div className="text-sm">
              {displayValue !== "N/A" ? (
                <span className="font-medium">{displayValue}</span>
              ) : (
                <span className="text-gray-400">N/A</span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Phone Number",
        accessor: "demographic_profile_of_respondents.contact_info_phone",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.contact_info_phone",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return (
            <div className="text-sm font-mono">
              {displayValue !== "N/A" ? (
                <span>{displayValue}</span>
              ) : (
                <span className="text-gray-400">N/A</span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Date",
        accessor: "dt_month_date",
        Cell: ({ value }: any) => <div className="text-sm">{value || "N/A"}</div>,
      },
      {
        Header: "Surveyor",
        accessor: "surveyor_name",
        Cell: ({ row }: any) => {
          const doc = row.original;
          return (
            <div className="text-sm">
              <div className="font-medium">{doc.surveyor_name || "N/A"}</div>
            </div>
          );
        },
      },
      {
        Header: "Gender",
        accessor: "demographic_profile_of_respondents.gender_gender_select",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.gender_gender_select",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return <div className="text-sm">{displayValue}</div>;
        },
      },
      {
        Header: "Age Range",
        accessor: "demographic_profile_of_respondents.age_range_age_select",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.age_range_age_select",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return <div className="text-sm">{displayValue}</div>;
        },
      },
      {
        Header: "Background",
        accessor: "demographic_profile_of_respondents.professional_background_background_select",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.professional_background_background_select",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return <div className="text-sm">{displayValue}</div>;
        },
      },
      {
        Header: "Experience",
        accessor: "demographic_profile_of_respondents.professional_experience_experience_select",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.professional_experience_experience_select",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return <div className="text-sm">{displayValue}</div>;
        },
      },
      {
        Header: "City",
        accessor: "demographic_profile_of_respondents.metro_city_city_select",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.metro_city_city_select",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return <div className="text-sm">{displayValue}</div>;
        },
      },
      {
        Header: "Preference",
        accessor: "demographic_profile_of_respondents.political_preference_preference_select",
        Cell: ({ row }: any) => {
          const value = safe_get(
            row.original,
            "demographic_profile_of_respondents.political_preference_preference_select",
            null
          );
          const displayValue =
            value !== null && value !== undefined && value !== ""
              ? value
              : "N/A";
          return <div className="text-sm">{displayValue}</div>;
        },
      },
    ],
    [rowTransformers]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: documents,
      initialState: { pageSize: 20 },
    },
    usePagination
  );

  if (documents.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
        <div className="text-6xl mb-4">📋</div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          No Documents Found
        </h3>
        <p className="text-gray-600">
          Try adjusting your filters to see more results
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 h-full flex flex-col border border-gray-100">
      <div className="flex items-center gap-2 mb-6">
        <div className="h-1 w-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
        <h3 className="text-xl font-bold text-gray-800">
          Filtered Documents
        </h3>
        <span className="ml-2 px-3 py-1 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 rounded-full text-sm font-semibold">
          {documents.length}
        </span>
      </div>
      <div className="flex-1 overflow-auto rounded-lg border border-gray-200">
        <table {...getTableProps()} className="w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200"
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const mapped = isMapped(row.original.id);
              return (
                <tr
                  {...row.getRowProps()}
                  className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer transition-all duration-200 ${mapped ? "bg-green-50/30" : ""
                    } ${rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(row.original);
                    }
                  }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className="px-4 py-3 text-sm text-gray-700"
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {documents.length > 20 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <span className="text-sm font-medium text-gray-700">
            Page <strong className="text-indigo-600">{pageIndex + 1}</strong> of{" "}
            <strong>{pageOptions.length}</strong>
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="inline-flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg shadow-sm text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="inline-flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg shadow-sm text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
